import React, { useState, useEffect, useMemo } from "react";
import { connect } from "react-redux";
import {
  Box,
  Typography,
  Paper,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  Button,
  CircularProgress,
  useTheme,
  useMediaQuery,
} from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import ShowChartIcon from "@mui/icons-material/ShowChart";
import { Document, Page, pdfjs } from "react-pdf";
import moment from "moment";

import reultsService from "../../../Services/resultsService";
import "react-pdf/dist/esm/Page/TextLayer.css";

pdfjs.GlobalWorkerOptions.workerSrc = `//unpkg.com/pdfjs-dist@${pdfjs.version}/legacy/build/pdf.worker.min.js`;

function Ecgs(props) {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));

  const [ecgSelected, setEcgSelected] = useState(null);
  const [pdfString, setPdfString] = useState(null);
  const [loading, setLoading] = useState(false);

  // 1. שימוש ב-useMemo כדי למנוע יצירה מחדש של הרשימה בכל רינדור
  const resultList = useMemo(() => {
    return props.selectedPatient
      ? props.ecgsResults?.filter(
          (a) => a.custid == props.selectedPatient.custid,
        )
      : props.ecgsResults || [];
  }, [props.selectedPatient, props.ecgsResults]);

  // 2. אפקט לקביעת הבחירה הראשונית בלבד
  useEffect(() => {
    if (resultList.length > 0 && !ecgSelected) {
      setEcgSelected(resultList[0]);
    }
  }, [resultList]); // ירוץ רק כשהרשימה עצמה משתנה

  // 3. אפקט לטעינת ה-PDF - ירוץ *רק* כשה-ID של האק"ג משתנה
  useEffect(() => {
    if (ecgSelected?.ecgid) {
      setLoading(true);
      reultsService
        .getEcgFile(ecgSelected.ecgid)
        .then((p) => {
          setPdfString(p.data);
          setLoading(false);
        })
        .catch((err) => {
          console.error("Error fetching PDF:", err);
          setLoading(false);
        });
    }
  }, [ecgSelected?.ecgid]); // חשוב: תלות רק ב-ID ולא בכל האובייקט או ברשימה

  const downloadPDF = () => {
    if (!pdfString) return;
    const linkSource = `data:application/pdf;base64,${pdfString}`;
    const downloadLink = document.createElement("a");
    downloadLink.href = linkSource;
    downloadLink.download = `shl_ecg_${moment(ecgSelected?.devdatetime).format("DD_MM_YYYY_HH_mm")}.pdf`;
    downloadLink.click();
  };

  return (
    <Box
      sx={{
        width: "100%",
        maxWidth: "1100px",
        margin: "0 auto",
        p: { xs: 2, md: 4 },
        direction: "rtl",
      }}
    >
      <Typography
        variant="h4"
        sx={{
          fontWeight: 800,
          mb: 4,
          color: "text.primary",
          textAlign: "left",
        }}
      >
        בדיקות אק"ג
      </Typography>

      {resultList.length === 0 ? (
        <Paper sx={{ p: 5, textAlign: "center", borderRadius: 3 }}>
          <Typography variant="h6" color="textSecondary">
            אין נתונים להצגה
          </Typography>
        </Paper>
      ) : (
        <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
          <Paper
            elevation={2}
            sx={{
              p: 2.5,
              borderRadius: 3,
              borderRight: `6px solid ${theme.palette.primary.main}`,
              backgroundColor: "background.paper",
            }}
          >
            <Box
              sx={{
                display: "flex",
                flexDirection: { xs: "column", md: "row" },
                alignItems: "center",
                gap: 2,
              }}
            >
              <Button
                variant="contained"
                startIcon={<DownloadIcon sx={{ ml: 1, mr: 0 }} />}
                onClick={downloadPDF}
                disabled={!pdfString || loading}
                sx={{
                  borderRadius: "12px",
                  height: "56px",
                  px: 4,
                  fontWeight: 700,
                  width: { xs: "100%", md: "auto" },
                }}
              >
                הורד תרשים PDF
              </Button>

              <Box sx={{ flex: 1, width: "100%" }}>
                <FormControl fullWidth variant="outlined">
                  <InputLabel id="select-ecg-label">
                    בחר תאריך בדיקה לצפייה
                  </InputLabel>
                  <Select
                    labelId="select-ecg-label"
                    value={ecgSelected?.ecgid || ""}
                    label="בחר תאריך בדיקה לצפייה"
                    onChange={(e) => {
                      const selected = resultList.find(
                        (item) => item.ecgid === e.target.value,
                      );
                      setEcgSelected(selected);
                    }}
                    sx={{ borderRadius: "12px" }}
                  >
                    {resultList.map((e, i) => (
                      <MenuItem key={e.ecgid + i} value={e.ecgid}>
                        <Box
                          sx={{
                            display: "flex",
                            justifyContent: "space-between",
                            width: "100%",
                            alignItems: "center",
                          }}
                        >
                          <Typography
                            variant="body2"
                            color="textSecondary"
                            sx={{ mr: 4 }}
                          >
                            {i + 1}#
                          </Typography>
                          <Typography sx={{ fontWeight: 600 }}>
                            {moment(e.devdatetime).format("DD/MM/YYYY HH:mm")}
                          </Typography>
                        </Box>
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>
            </Box>
          </Paper>

          <Paper
            elevation={3}
            sx={{
              p: { xs: 1, md: 3 },
              borderRadius: 3,
              minHeight: "600px",
              backgroundColor:
                theme.palette.mode === "dark" ? "#1e1e1e" : "#f5f5f5",
              display: "flex",
              flexDirection: "column",
              overflow: "hidden",
            }}
          >
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                gap: 1.5,
                mb: 2.5,
                color: "text.primary",
              }}
            >
              <ShowChartIcon color="primary" />
              <Typography variant="h6" sx={{ fontWeight: 600 }}>
                תרשים אק"ג |{" "}
                {ecgSelected
                  ? moment(ecgSelected.devdatetime).format("DD/MM/YYYY HH:mm")
                  : ""}
              </Typography>
            </Box>

            <Box
              sx={{
                flex: 1,
                display: "flex",
                justifyContent: "center",
                bgcolor: "background.paper",
                borderRadius: 2,
                overflow: "auto",
                p: { xs: 0, md: 2 },
              }}
            >
              {loading ? (
                <Box
                  sx={{
                    display: "flex",
                    flexDirection: "column",
                    alignItems: "center",
                    py: 20,
                  }}
                >
                  <CircularProgress color="primary" sx={{ mb: 2 }} />
                  <Typography color="textSecondary">
                    טוען תרשים אק"ג...
                  </Typography>
                </Box>
              ) : pdfString ? (
                <Document
                  file={`data:application/pdf;base64,${pdfString}`}
                  loading={<CircularProgress />}
                >
                  <Page
                    pageNumber={1}
                    renderTextLayer={false}
                    renderAnnotationLayer={false}
                    width={isMobile ? window.innerWidth * 0.8 : 850}
                  />
                </Document>
              ) : (
                <Typography sx={{ mt: 10 }} color="textSecondary">
                  לא נבחר קובץ להצגה
                </Typography>
              )}
            </Box>
          </Paper>
        </Box>
      )}
    </Box>
  );
}

const mapStateToProps = (state) => ({
  ecgsResults: state.ecgsResults,
  selectedPatient: state.selectedPatient,
});

export default connect(mapStateToProps)(Ecgs);
